import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Lightning, Target, Books } from "@phosphor-icons/react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/9d29e252-9a73-4ced-9cad-f6f3ec842c8a/images/81a04ae51b7f97e38d3cd615020d86d36a48315920d2938eba5ac1170a18fbcb.png";

const pillars = [
  { icon: Lightning, label: "Strength", body: "Compound lifts with skill-tier alternates for any equipment." },
  { icon: Target, label: "Endurance", body: "Run, ruck, and interval prescriptions modeled after H2F doctrine." },
  { icon: ShieldCheck, label: "Mobility", body: "Joint-by-joint primers, prehab and recovery sessions baked in." },
  { icon: Books, label: "ACFT Prep", body: "Sprint-Drag-Carry, SPT, HRP, plank, and 2MR tuned to standards." },
];

export default function Landing() {
  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
              <div className="w-9 h-9 grid place-items-center bg-volt text-black font-display font-black text-base">TP</div>
            <div className="leading-none">
              <div className="font-display text-2xl font-black tracking-tight">TACTICAL</div>
              <div className="label-mono">PROGRESSION</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/community" data-testid="header-community-link" className="hidden sm:inline-flex px-4 py-2 border border-border uppercase text-xs tracking-widest hover:border-volt hover:text-volt">Community</Link>
            <Link to="/login" data-testid="header-login-link" className="px-4 py-2 border border-border uppercase text-xs tracking-widest hover:border-volt hover:text-volt">Login</Link>
          </div>
        </div>
      </header>

      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Tactical training" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/70 to-background"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-32">
          <div className="label-mono text-volt mb-6">// FIELD MANUAL 7-22 // HOLISTIC HEALTH AND FITNESS</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] max-w-4xl">
            TRAIN LIKE<br />
            THE <span className="text-volt">MISSION</span><br />
            DEPENDS ON IT.
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-2xl tracking-wide">
            A tactical training planner grounded in U.S. Army FM 7-22 doctrine. Week-by-week programs
            with skill-tiered exercise alternates, 310+ drills with written instructions, and an AI
            coach that auto-tunes every main lift to your logged 1RM.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/login" data-testid="hero-cta-login" className="group inline-flex items-center gap-3 bg-volt text-black px-7 py-4 font-display uppercase tracking-widest font-bold hover:bg-white transition-colors">
              Login & Build Plan <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      <section className="border-y border-border">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="label-mono mb-4">// FOUR PILLARS</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black mb-12">EVERY DOMAIN. EVERY DAY.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {pillars.map((p) => (
              <div key={p.label} className="bg-background p-8 hover:bg-card transition-colors h-full">
                <p.icon size={36} weight="bold" className="text-volt mb-6" />
                <div className="font-display text-2xl font-black mb-2">{p.label.toUpperCase()}</div>
                <p className="text-sm text-muted-foreground tracking-wide">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="label-mono mb-4">// AI COACH</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black mb-6">DRAFT A 12-WEEK MESOCYCLE IN SECONDS.</h2>
          <p className="text-muted-foreground mb-8 tracking-wide">
            Tell the coach your goal, time horizon, level and equipment. It returns a structured
            week-by-week plan you can edit, save, and progress through.
          </p>
          <Link to="/login" className="inline-flex items-center gap-3 border border-volt text-volt px-6 py-3 uppercase font-display tracking-widest hover:bg-volt hover:text-black transition-colors">
            Start Building <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
        <div className="border border-border bg-card p-8">
          <div className="label-mono text-volt mb-4">// SAMPLE MICROCYCLE — WEEK 1</div>
          <ul className="space-y-3 font-mono text-sm">
            {[
              ["MON", "STRENGTH LOWER", "Back Squat 5x5 · DB RDL 3x10"],
              ["TUE", "ENDURANCE", "60/120 Sprints · Mobility 10m"],
              ["WED", "STRENGTH UPPER", "Bench 5x5 · Pull-ups 4x8"],
              ["THU", "RECOVERY", "Mobility flow · 30m walk"],
              ["FRI", "ACFT FOCUS", "SDC · HRP · Plank ladder"],
              ["SAT", "RUCK", "4mi @ 35lbs"],
              ["SUN", "REST", "—"],
            ].map(([d, f, e]) => (
              <li key={d} className="grid grid-cols-[60px_140px_1fr] gap-4 border-b border-border/60 pb-2">
                <span className="text-volt">{d}</span>
                <span>{f}</span>
                <span className="text-muted-foreground">{e}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-border bg-card/40">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="creator-blurb">
          <div className="label-mono text-volt mb-3">// FROM THE CREATOR</div>
          <h2 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight mb-4">
            Designed &amp; Built By SGT Maximilian Heide
          </h2>
          <p className="text-muted-foreground tracking-wide leading-relaxed">
            Tactical Progression was designed and created by Sergeant Maximilian Heide — a Soldier
            who got tired of generic fitness apps that ignore Army doctrine. Every feature in this
            platform is grounded in U.S. Army Field Manual 7-22 (Holistic Health and Fitness),
            built for Soldiers, by a Soldier.
          </p>
        </div>
      </section>
    </div>
  );
}
